function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * QuerySuperEntry is a catch-all row renderer for event sections in the product (think of Funnels steps, Insights metrics, Retention)
 *
 * The idea behind this entry type is that multiple places could re-use the same workflows (event selection, filtering, etc..), it is the case
 * today that Insights is different enough that we need to rethink the structure of this component to make "metric" selection its own
 * entry type, as well as for Funnels steps
 *
 * TODO(pablo): break up all functionality from QueryEntrySection into their respective
 * selector type classes (SegmentEntry, CohortEntry, etc..)
 */

/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import { clsx } from 'clsx';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ChangeEvent } from 'common/components/select/types';
import { Placement } from 'common/components/tooltip/types';
import { EventItem } from 'common/query-builder/query-block/events-menu/types';
import { generateFilterTrackingProps } from 'common/query-builder/query-block/filter-menu/util';
import { PropertySectionItem } from 'common/query-builder/query-block/properties-menu/item-menu-types';
import { propertyItemToSegmentation, segmentationToPropertyItem } from 'common/query-builder/query-block/properties-menu/util';
import { isListOfObjectsSegment } from 'common/query-builder/query-block/segment-menu/types';
import { CountBySelection, MultiAttributionAggregator, RowNumericAggregator, RowNumericProperty, RowNumericSubAggregator } from 'common/query-builder/query-entry-section/components/aggregations';
import { BehaviorRowDateRange } from 'common/query-builder/query-entry-section/components/behaviors';
import { ControlBarAction, ControlBarActionItems, ControlBarActionValue, ControlSelectEntryPoint } from 'common/query-builder/query-entry-section/constants';
import { QueryEntrySectionTrackingEvent } from 'common/query-builder/query-entry-section/tracking';
import { Action, EntryPart, FiltersOperator, GroupEventBehaviors, isCohortEntry, isEventBasedEntry, isEventEntry, isQuerySuperEntry, isUserBasedEntry, QueryBehaviorEntry, QueryBucketableEntry, QueryEventEntry, QueryMetricEntry, QuerySuperEntry, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { PropertyType, ReportName, ResourceType } from 'common/report/constants';
import { PER_USER_AGGREGATIONS, PRIMITIVE_MATH_TYPES } from 'common/types/reports/bookmark';
import { QueryReason } from 'common/report/util/query-tracking/types';
import { Segmentation } from 'common/types/reports/query';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { NumberOperator } from 'common/widgets/property-filter-menu/models/types';
// eslint-disable-next-line import/no-cycle
import { CustomBucketSubitem } from 'common/query-builder/query-entry-section/components/custom-bucket-subitem';
import { RowFilters } from 'common/query-builder/query-entry-section/components/filters';
import { RowSelector } from 'common/query-builder/query-entry-section/components/selector';
import { StepRangeSelector } from 'common/widgets/step-range-selector';
import { getMathTypeFromEntry } from 'common/query-builder/query-builder/util';
import { RangeSelection } from 'common/widgets/step-range-selector/types';
import { useTranslation } from 'common/i18n/hooks';
import { SCDOffsetBlock } from 'common/query-builder/query-entry-section/components/scd-offset';
import { RevenueChangeBlock } from 'common/query-builder/query-entry-section/components/revenue-change';
import { MenuType, MultiMetricsSectionItem } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { isMetricLevelDataGroupsEnabled } from 'common/report/util/feature-sampling';
import { isProperty, isScdProperty } from 'common/query-builder/query-block/properties-menu/types';
import { TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { isTimeSeriesChartType } from 'common/report/insights/visualization/charts/chart-display/utils';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { DataGroupSelect } from 'common/query-builder/data-group-select';
import { useDataGroups } from 'common/contexts/react/data-group-context';
import { canShowMetricDataGroupSelect } from 'common/query-builder/query-entry-section/util/can-show-metric-data-group-select';
import { BaseEntryRow } from '../base';
import { isBehavioralFilterNegated } from '../behavior/utils';
import { SelectedOperator } from '../filter/constants';
import { FilterValueTemplate } from '../filter/types/index';
import { applyFilterOperatorToFilter, getFilterOperatorsSections, getFilterOperatorSymbol, getFilterOperatorTooltip } from '../filter/utils';
import { getEntryFilters, hasFirstTimeEventFilter, openCustomBucket, populateListPropertyAttrs, setEntryVisibility } from '../row-util';
import { SegmentListObjectPropertySelector } from '../segment-entry/components/segment-list-object-property-selector';
import type { EntryRowProps } from '../types';
import { getProperty, hasEventValue, hasPropertyValue } from './utils';
// eslint-disable-next-line import/no-cycle
import { helpers } from './helpers';
const NO_OPERATOR_TYPES = new Set<PropertyType>([PropertyType.Object, PropertyType.NonProperty]);

/**
 * QuerySuperEntryRow is a catch-all renderer for all type of rows
 */
export function SuperEntryRow(props: EntryRowProps<QuerySuperEntry | QueryEventEntry>) {
  var _currentProjectResour, _props$entry$event2, _props$entry$event3;
  const currentProjectResource = useCurrentProject();
  const currentProject = (_currentProjectResour = currentProjectResource.get()) === null || _currentProjectResour === void 0 ? void 0 : _currentProjectResour.value;
  const {
    t,
    print
  } = useTranslation();
  const dataGroupStore = useDataGroups();
  // Separate var in case we want a separate feature flag for rolling this out.
  const hasMetricLevelDataGroups = isMetricLevelDataGroupsEnabled(currentProject);
  const canShowDataGroupSelect = canShowMetricDataGroupSelect({
    currentProject,
    dataGroupStore
  });
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const [isTimePickerEnabled, setTimePickerEnabled] = useState(false);
  const entryFilter = "entryFilter" in props.entry && props.entry.entryFilter ? new PropertyFilter(props.entry.entryFilter) : null;

  /** Funnel behaviors (did A then B) are defined with frequency filters using the equals or at least numeric operators */
  const isFunnelBehavior = props.entry.behavioralFiltersOperator === FiltersOperator.Then;
  const isPlaceholder = helpers.isPlaceholder(props);

  /** @todo [pablo] consider displaying a disabled tooltip for these conditions instead of removing them from the UI */
  const displayEntryFilter = entryFilter && !hasFirstTimeEventFilter(props.entry) && !isBehavioralFilterNegated(props.entry as QueryBehaviorEntry);
  const displayEntryAggregations = (props.entry.type !== ResourceType.Cohort || props.hasNewUI) && props.sectionProps.metricsMode && (entryFilter ? displayEntryFilter : true);
  const canPerformPropertyAggregation = props.sectionProps.metricsMode && getProperty(props.entry);
  const canPerformListObjectPropertySelection = () => {
    const property = getProperty(props.entry);
    return canPerformPropertyAggregation && isListOfObjectsSegment(property);
  };
  const isDistinct = props.entry.aggregationOperator === PRIMITIVE_MATH_TYPES.UNIQUE_VALUES || props.entry.aggregationOperatorPerUser === PER_USER_AGGREGATIONS.UNIQUE_VALUES;
  // "Distinct Count" is the only aggregation operator that doesn't require the property to be a number.
  const canSelectOnlyNumericProperties = !isDistinct;
  const useNewMultiMetricsMenu = props.sectionProps.reportName === ReportName.Insights && isQuerySuperEntry(props.entry) && (isUserBasedEntry(props.entry) || isCohortEntry(props.entry) || isEventEntry(props.entry));
  const handleControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.CustomizeBuckets:
        openCustomBucket(props, ControlSelectEntryPoint.ControlBar);
        break;
      case ControlBarAction.Show:
      case ControlBarAction.Hide:
        setEntryVisibility(props, value.action === ControlBarAction.Show);
        break;
      case ControlBarAction.AddGroupedEvent:
        if (props.sectionProps.groupEventBehavior === GroupEventBehaviors.Append) {
          props.addEntry(ResourceType.Event, props.index + 1);
        } else {
          props.updateEntry(Action.Group);
        }
        break;
      case ControlBarAction.SetLookbackPeriod:
        props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
          property: _objectSpread(_objectSpread({}, props.entry.property), {}, {
            scdOffset: isTimeSeriesChartType(props.sectionProps.chartType) ? {
              value: -1,
              unit: props.sectionProps.dateUnit as unknown as TimeUnit
            } : {
              value: "$start",
              unit: props.sectionProps.dateUnit as unknown as TimeUnit
            }
          })
        }));
        break;
      case ControlBarAction.UnsetLookbackPeriod:
        props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
          property: _objectSpread(_objectSpread({}, props.entry.property), {}, {
            scdOffset: undefined
          })
        }));
        break;
      default:
        props.onControlSelect(value);
    }
  };

  /**
   * Handles filter updates from a nested QueryEntrySection
   */
  const handleFilterSectionUpdate = (filters: PropertyFilter[], filtersOperator: FiltersOperator, queryReason: QueryReason) => {
    props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
      filters,
      filtersOperator
    }), {
      queryReason
    });
  };
  const updateEntryFilter = function updateEntryFilter(filter: PropertyFilter) {
    let trackingProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    // focus on filter value when operator changes filter to an invalid state
    if (!filter.isValid()) {
      props.setActivePart(EntryPart.EntryFilterValue, "open");
    }
    const newEntry = _objectSpread(_objectSpread({}, props.entry), {}, {
      entryFilter: filter
    });
    props.updateEntry(Action.Edit, newEntry, {
      trackingProps: trackingProps !== null && trackingProps !== void 0 ? trackingProps : generateFilterTrackingProps(filter)
    });
    props.trackAction(Action.EditFilter, newEntry);
  };

  /**
   * Handles the case where a custom property entity is removed and the given entry
   * is a property aggregation type, convert it to a total count since the property
   * is no longer in a valid state
   */
  const handleEntryPropertyRemoved = () => {
    const newEntry = _objectSpread(_objectSpread({}, props.entry), {}, {
      aggregationOperator: props.getEntryDefaultOperator(),
      property: null
    });
    props.updateEntry(Action.Edit, newEntry);
    props.dispatchTrack(QueryEntrySectionTrackingEvent.ClearNumericProperty, _objectSpread({}, props.getEntryTrackingProps(newEntry)));
  };

  /**
   * Updates property aggregation selection on an entry
   */
  const handleEntryPropertySelected = (property: PropertySectionItem) => {
    if (!property) {
      return handleEntryPropertyRemoved();
    }
    const segmentationProp = populateListPropertyAttrs(propertyItemToSegmentation(property, property.resourceType));
    if (isListOfObjectsSegment(segmentationProp)) {
      property = segmentationToPropertyItem(segmentationProp) as PropertySectionItem;
      props.setActivePart(EntryPart.SubValue, "open");
    }
    const newEntry = _objectSpread(_objectSpread({}, props.entry), {}, {
      property
    });
    props.updateEntry(Action.Edit, newEntry);
    props.dispatchTrack(QueryEntrySectionTrackingEvent.SelectNumericProperty, _objectSpread(_objectSpread({}, props.getEntryTrackingProps(newEntry)), {}, {
      'Property name': newEntry.property.label,
      'Property data type': "type" in newEntry.property && newEntry.property.type
    }));
  };

  /**
   * Handles list of object sub property aggregations selections
   */
  const handleListObjectPropertySelect = (selection: Segmentation) => {
    const property = _objectSpread(_objectSpread({}, getProperty(props.entry)), {}, {
      listItemGroup: selection
    });
    props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
      property: segmentationToPropertyItem(property) as PropertySectionItem
    }));
  };
  const handleEntryFilterOperatorUpdate = (ev: ChangeEvent<SelectedOperator>) => {
    const [filterOperator] = ev.detail.item.value;
    const filter = applyFilterOperatorToFilter(filterOperator, entryFilter);
    updateEntryFilter(filter);
  };
  const handleEntryFilterValueUpdate = (attribute: keyof PropertyFilter, value) => {
    updateEntryFilter(entryFilter.setAttribute(attribute, value));
  };
  const handleSelectMultiMetrics = (sectionItem: MultiMetricsSectionItem) => {
    props.onEntryRowSelect({
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: []
    }, props.entry, sectionItem, props.index);
  };
  const handleCreateInline = (menuType: MenuType) => {
    props.onEntryRowCreate({
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: []
    }, menuType);
  };
  const exclusionStepEl = hasEventValue(props.entry) && props.isExclusionStepsSection && <StepRangeSelector selected={{
    isEntireRange: props.entry.excludeAll,
    range: props.entry.excludeRange
  }} numSteps={props.sectionProps.numExclusionSteps} rowLabelType={props.sectionProps.rowLabelTypeForExclusionSteps} theme={SelectTheme.Tertiary} label={!props.hasNewUI ? "between" : undefined} onSelect={(ev: CustomEvent<RangeSelection>) => {
    const {
      isEntireRange: excludeAll,
      range: excludeRange
    } = ev.detail;
    props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
      excludeAll,
      excludeRange
    }));
    props.dispatchTrack(QueryEntrySectionTrackingEvent.EditExclusionStepRange, {
      'Exclude all': excludeAll,
      'Exclude from': excludeRange.from || null,
      'Exclude to': excludeRange.to || null
    });
  }} />;
  const FilterValue = displayEntryFilter && FilterValueTemplate[entryFilter.propertyType];
  const selectorRenderer = () => {
    var _props$entry$event;
    return <>
      {/** main selector */}
      {useNewMultiMetricsMenu ? <RowSelector {...props} contextPanelParentElement={entryEl} isPlaceholder={!hasEventValue(props.entry)} selected={{
        menuType: isCohortEntry(props.entry) ? MenuType.Cohort : isUserBasedEntry(props.entry) ? MenuType.Profile : MenuType.Event,
        value: props.entry.event
      }} selectorType={SelectorTypeDefinitions.Metrics} onSelect={(sectionItem: MultiMetricsSectionItem) => handleSelectMultiMetrics(sectionItem)} onCreate={(ev: {
        detail: {
          item: MenuType;
        };
      }) => handleCreateInline(ev.detail.item)} /> : <RowSelector {...props} contextPanelParentElement={entryEl} isPlaceholder={!hasEventValue(props.entry)} />}

      {/** exclusion event range selector */}
      {!props.hasNewUI && exclusionStepEl}

      {/** numeric aggregation */}
      {displayEntryAggregations && !props.hasNewUI && <RowNumericAggregator {...props} />}

      {/** numeric property selection */}
      {!props.hasNewUI && canPerformPropertyAggregation && <RowNumericProperty {...props as EntryRowProps<QueryMetricEntry>} contextPanelParentElement={entryEl} isPlaceholder={!hasPropertyValue(props.entry)} handleSelect={handleEntryPropertySelected} />}

      {!props.hasNewUI && canPerformListObjectPropertySelection() && <SegmentListObjectPropertySelector {...props} segment={getProperty(props.entry)} hideOverflowControls handleSelect={handleListObjectPropertySelect} numericOnly={canSelectOnlyNumericProperties} />}

      {/* @todo [pablo] remove this to use behavioral Segmentation components */}
      {/** filter operator */}
      {displayEntryFilter && !NO_OPERATOR_TYPES.has(entryFilter.propertyType) && <SelectReact elref='filterOperator' menuWidth={200} menuHeight={400} selectedOption={print(getFilterOperatorSymbol(entryFilter))} theme={SelectTheme.Select} tooltip={getFilterOperatorTooltip(entryFilter)} className={clsx("filter-operator", "for-".concat(entryFilter.propertyType), "for-".concat(kebabCase(entryFilter.filterOperator), "-operator"))} sections={getFilterOperatorsSections(entryFilter, isFunnelBehavior ? [{
        value: NumberOperator.AtLeast
      }, {
        value: NumberOperator.EqualTo
      }] : null)} onChange={(ev: ChangeEvent) => handleEntryFilterOperatorUpdate(ev)} />}

      {/* @todo [pablo] remove this to use behavioral Segmentation components */}
      {/** render value selection only for non-cohort filters */}
      {FilterValue && <FilterValue filter={entryFilter} isActive={props.activePart === EntryPart.FilterValue} onChange={value => handleEntryFilterValueUpdate("filterValue", value)} onSelectMenuChange={ev => props.onSelectMenuChange(ev, EntryPart.FilterValue)} setActiveEntry={state => props.setActivePart(EntryPart.FilterValue, state)} updateEntry={updateEntryFilter} eventNames={hasEventValue(props.entry) ? [(_props$entry$event = props.entry.event as EventItem) === null || _props$entry$event === void 0 ? void 0 : _props$entry$event.value] : []} projectId={props.sectionProps.projectId} workspaceId={props.sectionProps.workspaceId} dataGroupId={props.sectionProps.dataGroupId} userId={props.sectionProps.userId} isTimePickerEnabled={isTimePickerEnabled} setTimePickerEnabled={setTimePickerEnabled} />}

      {/* @todo [pablo] remove this to use behavioral Segmentation components */}
      {/** date range selector */}
      {props.isDateRangeType && <BehaviorRowDateRange {...props as EntryRowProps<QueryBehaviorEntry>} contextPanelParentElement={entryEl} />}

      {/** 2-level aggregation */}
      {displayEntryAggregations && !props.hasNewUI && <RowNumericSubAggregator {...props as EntryRowProps<QueryMetricEntry>} />}
    </>;
  };
  const rowFilters = <RowFilters entry={props.entry as QueryMetricEntry} eventNames={(_props$entry$event2 = props.entry.event as EventItem) !== null && _props$entry$event2 !== void 0 && _props$entry$event2.value ? [(_props$entry$event3 = props.entry.event as EventItem) === null || _props$entry$event3 === void 0 ? void 0 : _props$entry$event3.value] : []} filters={getEntryFilters(props.entry, props.sectionProps.behaviorMode)[EntryPart.Filters]} index={props.index} onUpdate={handleFilterSectionUpdate} hideEvents={!isEventBasedEntry(props.entry)} hasNewUI={props.hasNewUI} nestedSectionParams={props.nestedSectionParams} sectionProps={props.sectionProps} placeholders={props.placeholder} onAbortStage={() => props.setActivePart(EntryPart.Filters, "close")} metricLevelDataGroups={hasMetricLevelDataGroups} />;
  const bodyRowRenderer = () => {
    var _props$entry$event4;
    const showDataGroupSelect = canShowDataGroupSelect && displayEntryAggregations && props.hasNewUI && !((_props$entry$event4 = props.entry.event as EventItem) !== null && _props$entry$event4 !== void 0 && _props$entry$event4.id);
    return <>
        {/* custom bucketing controls */}
        <CustomBucketSubitem {...props} entry={props.entry as QueryBucketableEntry} data-sentry-element="CustomBucketSubitem" data-sentry-source-file="index.tsx" />

        {/** inline filters */}
        {!props.hasNewUI && rowFilters}

        <div className={clsx("count-wrapper", {
        'has-dg-select': showDataGroupSelect
      })}>
          {showDataGroupSelect && <DataGroupSelect selectedDataGroup={props.entry.dataGroupId} onSelect={id => {
          const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
            dataGroupId: id
          });
          props.updateEntry(Action.Edit, entry);
        }} />}
          {displayEntryAggregations && props.hasNewUI && <CountBySelection {...props} withDataGroupSelect={showDataGroupSelect} />}
        </div>

        {props.hasNewUI && <SCDOffsetBlock property={props.entry.property} onChange={property => {
        props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
          property
        }));
      }} />}
        {props.hasNewUI && isProperty(props.entry.property) && <RevenueChangeBlock behavior={props.entry.property.behavior} onChange={updatedBehavior => {
        props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
          property: _objectSpread(_objectSpread({}, props.entry.property), {}, {
            behavior: updatedBehavior
          })
        }));
      }} />}
        <MultiAttributionAggregator {...props} data-sentry-element="MultiAttributionAggregator" data-sentry-source-file="index.tsx" />

        {/** (for funnels only) exclusion event range selector */}
        {props.hasNewUI && exclusionStepEl}
      </>;
  };

  /** inline filters */
  const suffixRenderer = props.hasNewUI && (() => rowFilters);

  // renders a filter button shortcut
  const actionsRenderer = props.hasNewUI && props.getFilteringCapabilities().canApplyFilter && (() => <>
        <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' tooltip={{
      'delay-in': 10,
      'delay-out': 10,
      placement: Placement.Top,
      text: t("query-builder.actions.filter")
    }} onClick={() => props.showPlaceholder(EntryPart.Filters)} />
      </>);
  return <BaseEntryRow ref={setEntryEl} {...props} renderActions={actionsRenderer} renderSelector={selectorRenderer} renderSuffix={suffixRenderer} renderBody={bodyRowRenderer} isPlaceholder={helpers.isPlaceholder(props)} onControlSelect={handleControlSelect} overflowControls={[...props.getCommonOverflowControls(), isScdProperty(props.entry.property) && !props.entry.property.scdOffset && ControlBarActionItems[ControlBarAction.SetLookbackPeriod], isScdProperty(props.entry.property) && props.entry.property.scdOffset && ControlBarActionItems[ControlBarAction.UnsetLookbackPeriod],
  // metric hiding
  props.sectionProps.enableHideEntry && !isPlaceholder && ControlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],
  // customize range
  props.sectionProps.canCustomizeNumericBuckets && getMathTypeFromEntry(props.entry) === PRIMITIVE_MATH_TYPES.HISTOGRAM && ControlBarActionItems[ControlBarAction.CustomizeBuckets],
  // funnels group events (event comparison)
  props.entry.type === ResourceType.Event && props.sectionProps.groupEventBehavior !== GroupEventBehaviors.None && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.AddGroupedEvent]), props.sectionProps.disableAddGroupedEventReason && !props.isStaged ? {
    tooltip: {
      text: props.sectionProps.disableAddGroupedEventReason
    },
    isDisabled: true
  } : {})]} data-sentry-element="BaseEntryRow" data-sentry-component="SuperEntryRow" data-sentry-source-file="index.tsx" />;
}